import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputRightAddon,
  Select,
  Stack,
  Switch,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Fragment, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { AgentToolDescriptor, AuditedValues, RiskTier } from "./agentTypes";
import { type RefusalReason, withBodyFolded } from "./tools";
import { weightSumOf } from "./requestDepartures";
import type { WeightDeparture } from "./requestDepartures";
import { useFieldErrorTranslation } from "../editing/save-coordinator/useFieldErrorTranslation";
import { skipToken } from "@reduxjs/toolkit/query";
import { ExtensionDurationUnit, Regulation, ServiceCategory, useGetSourcingEventQuery, useListLibraryElementsQuery } from "../../autogen/bff-api";
import { SERVICE_CATEGORIES, serviceCategoryLabel } from "../procurement/serviceCategory";
import type { LibraryElementDto } from "../../autogen/bff-api";
import {
  addedItemFields,
  awardCriteriaPlan,
  currentValueAt,
  entityLabel,
  flattenLeaves,
  formatChangedLeafValue,
  formatLeafValue,
  isLabelled,
  confirmedDeadlinePath,
  extensionFieldOf,
  isExtensionList,
  isRichTextDocument,
  leafLabelKey,
  listChange,
  listItemLabel,
  isPlainObject,
} from "./confirmationCardFormatting";
import type { ExtensionField, FormatLeafOptions, LeafEntry } from "./confirmationCardFormatting";
import type { FilledInValue } from "./filledInValues";
import { momentLocaleFor } from "../formatting/i18nFormatters";
import { describeFieldSource, trimmedText as text } from "./fieldSourceDescription";
import type { FieldSourceDetails } from "./fieldSourceDescription";
import { choicesOnCard, editedValue, editorKindFor, isEmptyEdit, isMoney, isRequiredLeaf, withEdits } from "./confirmationCardEditing";
import type { CardEdit, ChoicesOnCard, Comparison, EditorKind } from "./confirmationCardEditing";
import { NumericInput } from "../input/NumericInput";
import { ThemedDatePicker } from "../input/DateTimeSelectors/ThemedDatePicker";
import { ThemedDateTimePicker } from "../input/DateTimeSelectors/ThemedDateTimePicker";
import { AutoResizeTextarea } from "../products/AutoResizeTextarea";
import { TipTap } from "../input/TipTap/TipTap";
import { getTiptapJsonFromMarkdown } from "../input/TipTap/markdownUtils";
import { documentCategoryLabel, isDocumentCategory, writesStepDocument } from "./stepDocument";
import { procedureTileKey } from "../procurement/publicProcurementOptions";

export type PendingConfirmation = {
  label: string;
  riskTier: RiskTier;
  descriptor: AgentToolDescriptor;
  args: Record<string, unknown>;
  // The planner's own accompanying text for the proposed call (see buildSystemPrompt) — a short
  // Norwegian summary of what will change, shown above the field breakdown.
  summary: string | null;
  filledIn?: FilledInValue[];
  refusedReasons?: RefusalReason[];
  departsFromRequest?: RefusalReason[];
  weightDepartures?: WeightDeparture[];
  current?: Record<string, unknown>;
};

type LookupEntity = (id: string) => Record<string, unknown> | undefined;

const isPrivateProcurement = (current: Record<string, unknown> | undefined, payload: Record<string, unknown>) =>
  payload.privateContext !== undefined ||
  (current?.eventContext as Record<string, unknown> | undefined)?.type === "Private";

const RULE_BOUND_FIELDS = new Set([
  "procedureType",
  "regulation",
  "bidSubmissionDeadline",
  "requestDeadline",
  "questionDaysBeforeBid",
  "questionDeadline",
]);

const setsProcedureOrDeadline = (value: unknown): boolean =>
  Array.isArray(value)
    ? value.some(setsProcedureOrDeadline)
    : isPlainObject(value) &&
      Object.entries(value).some(([key, inner]) => RULE_BOUND_FIELDS.has(key) || setsProcedureOrDeadline(inner));

const RECORD_LANGUAGE = "no";

const useLeafLabel = (toolName: string, isPrivate = false, language?: string): ((path: string[]) => string) => {
  const { t: shownT, i18n } = useTranslation();
  const t = language ? i18n.getFixedT(language) : shownT;
  const extensionFieldLabel = useExtensionFieldLabel(language);
  const labels: Record<string, string> = {
    Answer: t("Answer"),
    Title: t("Title"),
    Department: t("Department"),
    Description: t("Description"),
    "Internal note": t("Internal note"),
    "Winning bids": t("Winning bids"),
    "Estimated value": t("Estimated value"),
    "Contract type": t("Contract type"),
    Counterparty: t("Counterparty"),
    Value: t("Value"),
    "No end date": t("No end date"),
    Responsible: t("Responsible"),
    "Start date": t("Start date"),
    "End date": t("End date"),
    Name: t("Name"),
    "Organization number": t("Organization number"),
    "Street address": t("Street address"),
    "Post code": t("Post code"),
    City: t("City"),
    "What the document will contain": t("What the document will contain"),
    Chapters: t("Chapters"),
    "Chapters this part adds": t("Chapters this part adds"),
    "Chapters written now": t("Chapters written now"),
    "Chapters written shorter": t("Chapters written shorter"),
    "Project ID": t("Project ID"),
    "Bid deadline": t("Bid deadline"),
    "Request deadline": t("Request deadline"),
    "Question deadline, days before the bid deadline": t("Question deadline, days before the bid deadline"),
    "Reference id": t("Reference id"),
    "CPV codes": t("CPV codes"),
    Procedure: t("Procedure"),
    Regulation: t("Regulation"),
    "Contract duration": t("Contract duration"),
    "Place of performance": t("Place of performance"),
    "Qualification requirements": t("Qualification requirements"),
    "Minimum requirements": t("Minimum requirements"),
    "Award criteria": isPrivate ? t("Evaluation requirements") : t("Award criteria"),
    "Invited suppliers": t("Invited suppliers"),
    "Framework agreement": t("Framework agreement"),
    "Dynamic purchasing system": t("Dynamic purchasing system"),
    "Electronic auction": t("Electronic auction"),
    "Price model": t("Price model"),
    "Exclusion grounds": t("Exclusion grounds"),
    "Utility activity": t("Utility activity"),
    "What kind of service is the contract?": t("What kind of service is the contract?"),
    "Special services": t("Special services"),
    Products: t("Products"),
    "Requires documentation": t("Requires documentation"),
    "Part of the procurement regulations": t("Part of the procurement regulations"),
    "Contract period in months": t("Contract period in months"),
    Type: t("Type"),
    Weight: t("Weight"),
    "Answer type": t("Answer type"),
    "Answer format": t("Answer format"),
    Category: t("Category"),
    Rationale: t("Rationale"),
    "Justification for the environmental weighting": t("Justification for the environmental weighting"),
    "Tender validity": t("Tender validity"),
    "Accelerated procedure": t("Accelerated procedure"),
    "Sub-criteria": t("Sub-criteria"),
    Tags: t("Tags"),
    "Reserved participation": t("Reserved participation"),
    "Announcement date": t("Announcement date"),
    Criteria: t("Criteria"),
    "Lowest price": t("Lowest price"),
    "Best price-quality ratio": t("Best price-quality ratio"),
    "Options in the contract": t("Options in the contract"),
    Schedule: t("Schedule"),
    Projects: t("Projects"),
    "Justification for the procedure": t("Justification for the procedure"),
    "EU funding": t("EU funding"),
    "Languages for tenders": t("Languages for tenders"),
    "Alternative tenders": t("Alternative tenders"),
    "Climate consideration": t("Climate consideration"),
    "Maximum number of candidates": t("Maximum number of candidates"),
    "Minimum number of candidates": t("Minimum number of candidates"),
    "Maximum value of the framework agreement": t("Maximum value of the framework agreement"),
    "Option descriptions": t("Option descriptions"),
    "Bid deadline confirmed": t("Bid deadline confirmed"),
    "Request deadline confirmed": t("Request deadline confirmed"),
    "Tender deadline agreed with the candidates": t("Tender deadline agreed with the candidates"),
    "Announce on Doffin": t("Announce on Doffin"),
    Extensions: t("Extensions"),
  };
  return (path) => {
    const extension = extensionFieldOf(path);
    if (extension) return extensionFieldLabel(extension);
    const key = leafLabelKey(path, toolName);
    return labels[key] ?? t("Other field");
  };
};

const useExtensionFieldLabel = (language?: string): ((extension: ExtensionField) => string) => {
  const { t: shownT, i18n } = useTranslation();
  const t = language ? i18n.getFixedT(language) : shownT;
  return ({ number, field }) => {
    switch (field) {
      case "extension":
        return t("Extension {{number}}", { number });
      case "duration":
        return number === null ? t("Length") : t("Length of extension {{number}}", { number });
      case "unit":
        return number === null ? t("Unit") : t("Unit of extension {{number}}", { number });
      case "exerciseDeadlineMonths":
        return number === null ? t("Deadline to exercise") : t("Deadline to exercise extension {{number}}", { number });
    }
  };
};

const regulationAfter = (current: Record<string, unknown> | undefined, patch: Record<string, unknown>): unknown => {
  const proposed = (patch.publicContext as Record<string, unknown> | undefined)?.regulation;
  return proposed ?? (current?.eventContext as Record<string, unknown> | undefined)?.regulation;
};

const isServiceCategory = (value: string): value is ServiceCategory =>
  (SERVICE_CATEGORIES as readonly string[]).includes(value);

const bodyParamOf = (descriptor: AgentToolDescriptor) => descriptor.params.find((param) => param.in === "body");

type BodyField = { name: string; required: boolean };

const bodyFieldsOf = (schema: Record<string, unknown> | undefined): BodyField[] => {
  const properties = (schema?.properties as Record<string, unknown> | undefined) ?? {};
  const required = new Set((schema?.required as string[] | undefined) ?? []);
  return Object.keys(properties).map((name) => ({ name, required: required.has(name) }));
};

const ItemHeading = ({ children, removed = false }: { children: ReactNode; removed?: boolean }) => (
  <Text fontSize="sm" fontWeight="semibold" color={removed ? "smError" : "smText"} whiteSpace="pre-wrap">
    {children}
  </Text>
);

const ListValueView = ({
  entry,
  current,
  opts,
  replacedInFull,
  leafLabel,
}: {
  entry: LeafEntry;
  current: unknown;
  opts: FormatLeafOptions;
  replacedInFull: boolean;
  leafLabel: (path: string[]) => string;
}) => {
  const { t } = useTranslation();
  const proposed = entry.value as unknown[];
  const names = (items: unknown[]) => items.map((item) => listItemLabel(item, opts)).join(", ");
  const itemLeafLabel = (path: string[]) => leafLabel([...entry.path.slice(-1), ...path]);
  const byName = (items: unknown[]) => items.filter((item) => addedItemFields(item).length === 0);
  const withFields = (items: unknown[]) => items.filter((item) => addedItemFields(item).length > 0);
  const newItem = (item: unknown, heading: string) => (
    <Stack key={listItemLabel(item, opts)} spacing="0.5">
      <ItemHeading>{heading}</ItemHeading>
      {addedItemFields(item).map((field) => (
        <ChangedField key={field.path.join(".")} field={field} opts={opts} leafLabel={itemLeafLabel} />
      ))}
    </Stack>
  );
  if (!Array.isArray(current)) {
    if (entry.cleared) {
      return (
        <Text fontSize="sm" fontWeight="semibold" color="smText">
          {opts.clearedLabel}
        </Text>
      );
    }
    const listed = names(byName(proposed));
    return (
      <Stack spacing="0.5">
        {(listed || replacedInFull) && (
          <Text fontSize="sm" fontWeight="semibold" color="smText" whiteSpace="pre-wrap">
            {replacedInFull ? `${t("Replaced in full with")}:${listed ? ` ${listed}` : ""}` : listed}
          </Text>
        )}
        {withFields(proposed).map((item) => newItem(item, listItemLabel(item, opts)))}
      </Stack>
    );
  }
  const { added, removed, changed } = listChange(proposed, current);
  const addedByName = byName(added);
  return (
    <Stack spacing="0.5">
      {entry.cleared && (
        <Text fontSize="sm" fontWeight="semibold" color="smText">
          {opts.clearedLabel}
        </Text>
      )}
      {addedByName.length > 0 && (
        <Text fontSize="sm" fontWeight="semibold" color="smText" whiteSpace="pre-wrap">
          {t("Will be added")}: {names(addedByName)}
        </Text>
      )}
      {withFields(added).map((item) => newItem(item, `${t("Will be added")}: ${listItemLabel(item, opts)}`))}
      {removed.length > 0 && (
        <ItemHeading removed>
          {t("Will be removed")}: {names(removed)}
        </ItemHeading>
      )}
      {changed.map(({ item, fields }) => (
        <Stack key={listItemLabel(item, opts)} spacing="0.5">
          <ItemHeading>
            {t("Will be changed")}: {listItemLabel(item, opts)}
          </ItemHeading>
          {fields.map((field) => (
            <ChangedField key={field.path.join(".")} field={field} opts={opts} leafLabel={itemLeafLabel} />
          ))}
        </Stack>
      ))}
      {added.length === 0 && removed.length === 0 && changed.length === 0 && !entry.cleared && (
        <Text fontSize="sm" fontWeight="semibold" color="smText" whiteSpace="pre-wrap">
          {names(proposed)}
        </Text>
      )}
    </Stack>
  );
};

const AwardCriteriaView = ({
  proposed,
  current,
  opts,
  replacedInFull,
  leafLabel,
}: {
  proposed: Record<string, unknown>;
  current: unknown;
  opts: FormatLeafOptions;
  replacedInFull: boolean;
  leafLabel: (path: string[]) => string;
}) => {
  const { t } = useTranslation();
  const plan = awardCriteriaPlan(proposed, current, opts);
  if (plan.kind === "lowestPrice") {
    return (
      <Text fontSize="sm" fontWeight="semibold" color="smText">
        {plan.heading}
      </Text>
    );
  }
  const withWeight = (label: string, weight: string | null) => (weight ? `${label} (${weight})` : label);
  return (
    <Stack spacing="0.5">
      <Text fontSize="sm" fontWeight="semibold" color="smText">
        {replacedInFull ? `${t("Replaced in full with")}: ${plan.heading}` : plan.heading}
      </Text>
      {plan.rows.map((row) => (
        <Fragment key={`${row.change}:${row.label}`}>
        <Text
          fontSize="sm"
          fontWeight="semibold"
          color={row.change === "removed" ? "smError" : "smText"}
        >
          {row.change === "added" && `${t("Will be added")}: ${withWeight(row.label, row.weight)}`}
          {row.change === "removed" && `${t("Will be removed")}: ${withWeight(row.label, row.weight)}`}
          {row.change === "reweighted" && `${row.label}: ${row.previousWeight ?? "–"} → ${row.weight ?? "–"}`}
          {row.change === "changed" && `${t("Will be changed")}: ${withWeight(row.label, row.weight)}`}
          {row.change === "unchanged" && withWeight(row.label, row.weight)}
        </Text>
        {row.descriptionCleared && (
          <Box pl="3" borderLeftWidth="2px" borderColor="smBorder" mb="1">
            <Text fontSize="sm" color="smText">
              {t("New description")}: {t("Will be emptied")}
            </Text>
          </Box>
        )}
        {row.newDescription && row.change !== "removed" && (
          <Box pl="3" borderLeftWidth="2px" borderColor="smBorder" mb="1">
            <Text fontSize="sm" color="smMuted">
              {row.change === "added" ? t("Description") : t("New description")}
            </Text>
            <RichValue text={row.newDescription} />
          </Box>
        )}
        {row.details.length > 0 && (
          <Box borderLeftWidth="2px" borderColor="smBorder" mb="1">
            {row.details.map((field) => (
              <ChangedField key={field.path.join(".")} field={field} opts={opts} leafLabel={leafLabel} />
            ))}
          </Box>
        )}
        {row.subCriteria.map((sub) => (
          <Box key={`${sub.change}:${sub.label}`} pl="3" borderLeftWidth="2px" borderColor="smBorder" mb="1">
            <ItemHeading removed={sub.change === "removed"}>
              {sub.change === "added" && `${t("Sub-criterion will be added")}: ${sub.label}`}
              {sub.change === "changed" && `${t("Sub-criterion will be changed")}: ${sub.label}`}
              {sub.change === "removed" && `${t("Sub-criterion will be removed")}: ${sub.label}`}
            </ItemHeading>
            {sub.details.map((field) => (
              <Text key={field.path.join(".")} fontSize="sm" color="smText" whiteSpace="pre-wrap">
                {leafLabel(field.path)}: {formatChangedLeafValue(field, opts)}
                {field.path[0] === "weight" && sub.shareOfTotal && ` (${t("{{share}} % of the total", { share: sub.shareOfTotal })})`}
              </Text>
            ))}
            {sub.newDescription && sub.change === "changed" && (
              <Text fontSize="sm" color="smMuted">
                {t("New description")}
              </Text>
            )}
            {sub.newDescription && <RichValue text={sub.newDescription} />}
          </Box>
        ))}
        </Fragment>
      ))}
    </Stack>
  );
};

const FIELD_SOURCES = "fieldSources";

type ClaimedSource = FieldSourceDetails;
type FieldSourceClaim = { path: string; source: ClaimedSource };

const fieldSourceClaimsOf = (descriptor: AgentToolDescriptor, args: Record<string, unknown>): FieldSourceClaim[] => {
  const body = bodyParamOf(descriptor);
  const nested = body && args[body.name] && typeof args[body.name] === "object"
    ? (args[body.name] as Record<string, unknown>)[FIELD_SOURCES]
    : undefined;
  const declared = args[FIELD_SOURCES] ?? nested;
  if (!Array.isArray(declared)) return [];
  return declared.filter(
    (claim): claim is FieldSourceClaim =>
      !!claim &&
      typeof claim === "object" &&
      typeof claim.path === "string" &&
      !!claim.source &&
      typeof claim.source === "object" &&
      typeof claim.source.kind === "string",
  );
};

const writesFieldSources = (descriptor: AgentToolDescriptor): boolean =>
  descriptor.params.some(
    (param) =>
      param.name === FIELD_SOURCES ||
      (param.in === "body" && !!(param.schema.properties as Record<string, unknown> | undefined)?.[FIELD_SOURCES]),
  );

export const libraryElementTitle = (element: LibraryElementDto): string | null => {
  const { minimum, selection, award, awardSubCriterion } = element.content;
  return text(minimum?.title) ?? text(selection?.name) ?? text(award?.name) ?? text(awardSubCriterion?.name);
};

const listPosition = (segment: string): { name: string; position: number | null } => {
  const indexed = /^(.+)\[(\d+)\]$/.exec(segment);
  return indexed ? { name: indexed[1], position: Number(indexed[2]) + 1 } : { name: segment, position: null };
};

const valueAt = (root: unknown, segments: { name: string; position: number | null }[]): unknown =>
  segments.reduce<unknown>((value, { name, position }) => {
    if (!isPlainObject(value)) return undefined;
    const field = value[name];
    return position === null ? field : Array.isArray(field) ? field[position - 1] : undefined;
  }, root);

const FieldSourcesView = ({
  claims,
  assistantFilled,
  changed,
  leafLabel,
  libraryTitle,
  rulesSetProcedure,
}: {
  claims: FieldSourceClaim[];
  assistantFilled: string[];
  changed: unknown;
  leafLabel: (path: string[]) => string;
  libraryTitle: (id: string) => string | null;
  rulesSetProcedure: boolean;
}) => {
  const { t } = useTranslation();
  const describe = (source: ClaimedSource): string | null =>
    describeFieldSource(t, source, (details) =>
      typeof details.libraryElementId === "string" ? libraryTitle(details.libraryElementId) : null,
    );
  const fieldLabel = (path: string) => {
    const dotted = path.replace(/\[(\d+)\]/g, ".$1").split(".");
    if (extensionFieldOf(dotted)) return leafLabel(dotted);
    const segments = path.split(".").map(listPosition);
    const label = leafLabel(segments.map((segment) => segment.name));
    const itemAt = segments.findLastIndex((segment) => segment.position !== null);
    if (itemAt < 0) return label;
    const item = valueAt(changed, segments.slice(0, itemAt + 1));
    const itemName = isPlainObject(item) ? text(item.name) ?? text(item.title) : null;
    const isTheItem = itemAt === segments.length - 1;
    if (isTheItem) return itemName ?? `${label} ${segments[itemAt].position}`;
    return itemName
      ? `${itemName} – ${label.toLocaleLowerCase()}`
      : `${leafLabel(segments.slice(0, itemAt + 1).map((segment) => segment.name))} ${segments[itemAt].position} – ${label.toLocaleLowerCase()}`;
  };
  const shown = claims.flatMap((claim) => {
    const description = describe(claim.source);
    const labelled = isLabelled(claim.path.split(".").map((segment) => listPosition(segment).name));
    return description && labelled ? [{ path: claim.path, description }] : [];
  });
  const filled = assistantFilled.map((path) => ({ path, description: t("The assistant's suggestion") }));
  return (
    <Box mt="3">
      <Text fontSize="xs" color="smMuted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb="1">
        {t("Sources")}
      </Text>
      <Stack spacing="0.5">
        {[...shown, ...filled].map(({ path, description }) => (
          <Text key={`${path}:${description}`} fontSize="sm" color="smText">
            {fieldLabel(path)}: {description}
          </Text>
        ))}
        <Text fontSize="xs" color="smMuted">
          {rulesSetProcedure
            ? t("A field with no source here is the assistant's suggestion. The procedure and the deadlines follow the procurement rules.")
            : t("A field with no source here is the assistant's suggestion.")}
        </Text>
      </Stack>
    </Box>
  );
};

const isAwardCriteria = (entry: LeafEntry): entry is LeafEntry & { value: Record<string, unknown> } =>
  entry.path[entry.path.length - 1] === "awardCriteria" &&
  !entry.cleared &&
  typeof entry.value === "object" &&
  entry.value !== null &&
  !Array.isArray(entry.value);

type CardProps = {
  pending: PendingConfirmation;
  lookupEntity: LookupEntity;
  onConfirm: (args: Record<string, unknown>, values?: AuditedValues) => void;
  onCancel: () => void;
  isBusy: boolean;
  onEditsChange?: (editsLine: string | null) => void;
};

export const ConfirmationCard = (props: CardProps) => {
  const { descriptor, args } = props.pending;
  const eventId = descriptor.toolName === "updateSourcingEvent" && typeof args.eventId === "string" ? args.eventId : null;
  const proposedAgainst = props.pending.current;
  const { data: fetchedEvent, isLoading, isError } = useGetSourcingEventQuery(
    eventId && !proposedAgainst ? { eventId } : skipToken,
  );
  const currentEvent = proposedAgainst ?? fetchedEvent;
  const citesLibrary = fieldSourceClaimsOf(descriptor, args).some((claim) => claim.source.kind === "REQUIREMENT_LIBRARY");
  const { data: library } = useListLibraryElementsQuery(citesLibrary ? {} : skipToken);
  const libraryTitle = (id: string) => {
    const element = library?.elements.find((candidate) => candidate.id === id);
    return element ? libraryElementTitle(element) : null;
  };
  const documentEventId =
    writesStepDocument(descriptor.toolName) && typeof args.eventId === "string" ? args.eventId : null;
  const { data: documentEvent } = useGetSourcingEventQuery(documentEventId ? { eventId: documentEventId } : skipToken);
  const lookupEntity: LookupEntity = (id) =>
    props.lookupEntity(id) ?? (documentEvent && id === documentEvent.id ? { title: documentEvent.eventData.title } : undefined);
  const comparison: Comparison =
    !eventId ? "none" : proposedAgainst ? "ready" : isLoading ? "loading" : isError ? "failed" : "ready";
  return (
    <ConfirmationCardView
      {...props}
      lookupEntity={lookupEntity}
      current={currentEvent as Record<string, unknown> | undefined}
      comparison={comparison}
      libraryTitle={libraryTitle}
    />
  );
};

export type { Comparison };

export const ConfirmationCardView = ({
  pending,
  current,
  lookupEntity,
  onConfirm,
  onCancel,
  isBusy,
  onEditsChange,
  comparison = "none",
  libraryTitle = () => null,
}: CardProps & {
  current?: Record<string, unknown>;
  comparison?: Comparison;
  libraryTitle?: (id: string) => string | null;
}) => {
  const { t, i18n } = useTranslation();
  const locale = momentLocaleFor(i18n.language);
  const resolveEntityName = (id: string) => entityLabel(lookupEntity(id)) || undefined;

  const { riskTier } = pending;
  const { descriptor, args } = useMemo(() => withBodyFolded(pending.descriptor, pending.args), [pending.descriptor, pending.args]);
  const [edits, setEdits] = useState<Map<string, CardEdit>>(new Map());
  const binding = riskTier === "binding_statutory";
  const body = bodyParamOf(descriptor);
  const payload = body && typeof args[body.name] === "object" ? (args[body.name] as Record<string, unknown>) : {};
  const isPrivate = isPrivateProcurement(current, payload);
  const leafLabel = useLeafLabel(pending.descriptor.toolName, isPrivate);
  const recordedLeafLabel = useLeafLabel(pending.descriptor.toolName, isPrivate, RECORD_LANGUAGE);
  const recordT = i18n.getFixedT(RECORD_LANGUAGE);
  const regulation = regulationAfter(current, payload);
  const regulationKnown = current !== undefined || regulation !== undefined;
  const underUtilities = regulationKnown ? regulation === Regulation.Forsyningsforskriften : null;
  const choiceLabelAt = (path: string[], choice: string): string =>
    path[path.length - 1] === "serviceCategory" && isServiceCategory(choice)
      ? serviceCategoryLabel(t, choice, underUtilities)
      : (formatOptions.translateEnum?.(choice) ?? choice);

  const concerns = descriptor.params
    .filter((param) => param.in === "path")
    .map((param) => entityLabel(lookupEntity(typeof args[param.name] === "string" ? (args[param.name] as string) : "")))
    .filter((label) => label.length > 0);

  const fields = bodyFieldsOf(body?.schema).filter((field) => field.name !== FIELD_SOURCES);
  const fieldLeaves = (field: BodyField) => flattenLeaves([field.name], payload[field.name]);
  const labelledLeaves = (field: BodyField) => fieldLeaves(field).filter((entry) => isLabelled(entry.path));
  const present = fields.filter((field) => labelledLeaves(field).length > 0);
  const otherChanges = fields.flatMap(fieldLeaves).filter((entry) => !isLabelled(entry.path));
  const formatOptions: FormatLeafOptions = {
    locale,
    yesLabel: t("Yes"),
    noLabel: t("No"),
    clearedLabel: t("Will be emptied"),
    lowestPriceLabel: String(t("Lowest price")),
    bestPriceQualityRatioLabel: String(t("Best price-quality ratio")),
    monthsLabel: (count) => String(t("{{count}} months", { count })),
    periodFromLabel: (period, startDate) => String(t("{{period}} from {{startDate}}", { period, startDate })),
    extensionLabel: (extension, number) => {
      const { duration, unit, exerciseDeadlineMonths: deadline } = extension;
      const length =
        typeof duration !== "number"
          ? null
          : unit === ExtensionDurationUnit.Months
            ? `${duration} ${t("month", { count: duration })}`
            : unit === ExtensionDurationUnit.Years
              ? `${duration} ${t("year", { count: duration })}`
              : String(duration);
      const exercise =
        typeof deadline === "number"
          ? `${t("Deadline to exercise")}: ${t("{{count}} months before the contract ends", { count: deadline })}`
          : null;
      return `${t("Extension {{number}}", { number })}: ${[length, exercise].filter(Boolean).join(" · ")}`;
    },
    monthsBeforeEndLabel: (count) => String(t("{{count}} months before the contract ends", { count })),
    translateEnum: (value) =>
      i18n.exists(value) ? String(t(procedureTileKey(value))) : isDocumentCategory(value) ? documentCategoryLabel(value) : undefined,
    resolveEntityName,
  };
  const recordedFormatOptions: FormatLeafOptions = {
    ...formatOptions,
    locale: momentLocaleFor(RECORD_LANGUAGE),
    yesLabel: String(recordT("Yes")),
    noLabel: String(recordT("No")),
    clearedLabel: String(recordT("Will be emptied")),
    lowestPriceLabel: String(recordT("Lowest price")),
    bestPriceQualityRatioLabel: String(recordT("Best price-quality ratio")),
    monthsLabel: (count) => String(recordT("{{count}} months", { count })),
    monthsBeforeEndLabel: (count) => String(recordT("{{count}} months before the contract ends", { count })),
    translateEnum: (value) =>
      i18n.exists(value)
        ? String(recordT(procedureTileKey(value)))
        : isDocumentCategory(value)
          ? documentCategoryLabel(value)
          : undefined,
  };
  const valuesText = (from: Record<string, unknown>): string => {
    const values = body && typeof from[body.name] === "object" ? (from[body.name] as Record<string, unknown>) : {};
    return fields
      .flatMap((field) => flattenLeaves([field.name], values[field.name]))
      .filter((entry) => isLabelled(entry.path))
      .map((entry) => `${recordedLeafLabel(entry.path)}: ${formatLeafValue(entry, recordedFormatOptions)}`)
      .join("; ");
  };
  const edit = (path: string[], value: unknown) => {
    const next = new Map(edits).set(path.join("."), { path, value });
    setEdits(next);
    onEditsChange?.(describeEdits(next));
  };
  const describeEdits = (next: ReadonlyMap<string, CardEdit>) => {
    if (next.size === 0) return null;
    const changes = [...next.values()].map(({ path, value }) =>
      isEmptyEdit(value)
        ? `${leafLabel(path)}: ${t("Will be emptied")}`
        : `${leafLabel(path)}: ${formatLeafValue({ path, value }, formatOptions)}`,
    );
    return `${t("In the card I changed")}: ${changes.join("; ")}`;
  };
  const filledIn = pending.filledIn ?? [];
  const filledInText = (field: string, value: unknown) => {
    const path = field.split(".");
    const values = { field: leafLabel(path), value: formatLeafValue({ path, value }, formatOptions) };
    return extensionFieldOf(path)?.field === "exerciseDeadlineMonths"
      ? t("{{field}} is set to {{value}}, because every extension must have a deadline to exercise.", values)
      : t("{{field}} is set to {{value}}, because the field must have a value.", values);
  };
  const claims = fieldSourceClaimsOf(descriptor, args);
  const showsSources = writesFieldSources(descriptor);
  const emptiedRequired = [...edits.values()]
    .filter(({ path, value }) => isEmptyEdit(value) && isRequiredLeaf(body?.schema, path))
    .map(({ path }) => path);
  const isEmptiedRequired = (path: string[]) => emptiedRequired.some((emptied) => emptied.join(".") === path.join("."));
  const missing = fields.filter((field) => field.required && fieldLeaves(field).length === 0).map((field) => [field.name]);
  const comparesLists = present.some((field) =>
    labelledLeaves(field).some((entry) => Array.isArray(entry.value) || isAwardCriteria(entry)),
  );
  const comparisonPending = comparesLists && comparison === "loading";
  const comparisonFailed = comparesLists && comparison === "failed";
  const refused = (pending.refusedReasons?.length ?? 0) > 0;
  const fieldErrorText = useFieldErrorTranslation();
  const weightDepartures = pending.weightDepartures ?? [];
  const departsFromRequest = (refused ? [] : (pending.departsFromRequest ?? [])).filter(
    (reason) => weightDepartures.length === 0 || !isWeightField(reason.field),
  );
  const proposedSum = weightSumOf(descriptor, withEdits(args, body?.name, edits));
  const weightSum = proposedSum !== null && proposedSum !== 100 ? proposedSum : null;
  const departureText = (departure: WeightDeparture) => {
    const { label: criterion, proposed, reason } = departure;
    if ("stored" in departure) {
      const values = { criterion, stored: percent(departure.stored), proposed: percent(proposed) };
      return reason === "sumNot100"
        ? t("The criterion weights must add up to 100 %, so the card changes {{criterion}} from {{stored}} to {{proposed}}.", values)
        : t("The card changes {{criterion}} from {{stored}} to {{proposed}}.", values);
    }
    const values = { criterion, requested: percent(departure.requested), proposed: percent(proposed) };
    if (reason === "outOfRange") {
      return t("You asked for {{criterion}} {{requested}}. A weight must be between 1 and 100 %, so the card proposes {{proposed}}.", values);
    }
    if (reason === "sumNot100") {
      return t("You asked for {{criterion}} {{requested}}. The criterion weights must add up to 100 %, so the card proposes {{proposed}}.", values);
    }
    return t("You asked for {{criterion}} {{requested}}, but the card proposes {{proposed}}.", values);
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="xl" bg="smBackground" boxShadow="sm" p="4">
      <Flex align="center" justify="space-between" gap="3" mb="3">
        <Text fontSize="md" fontWeight="semibold" color="smText">
          {pending.label}
        </Text>
        <Badge
          bg={binding ? "smWarningSoft" : "smStatusNeutralBg"}
          color={binding ? "smWarning" : "smStatusNeutralText"}
          px="2"
          py="0.5"
          borderRadius="md"
          fontSize="xs"
          textTransform="none"
          flexShrink="0"
        >
          {binding ? t("Binding") : t("Reversible")}
        </Badge>
      </Flex>

      {pending.summary && (
        <Box mb="3" fontSize="sm" sx={{ ".tiptap p": { marginBottom: "0.5rem" }, ".tiptap p:last-child": { marginBottom: 0 } }}>
          <TipTap
            content={getTiptapJsonFromMarkdown(pending.summary)}
            editable={false}
            hasBorder={false}
            padding="0"
            maxHeight="full"
          />
        </Box>
      )}

      {binding && (
        <Text fontSize="xs" color="smWarning" mb="3">
          {t("Binding action — it may notify suppliers and start statutory deadlines.")}
        </Text>
      )}

      {concerns.length > 0 && (
        <HStack spacing="2" fontSize="sm" mb="3" pb="3" borderBottomWidth="1px" borderColor="smBorder">
          <Text color="smMuted" fontWeight="medium" flexShrink="0">
            {t("Concerns")}
          </Text>
          <Text color="smText" fontWeight="semibold">
            {concerns.join(", ")}
          </Text>
        </HStack>
      )}

      {present.length > 0 && (
        <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="smBackgroundSecondary" p="3">
          <Stack spacing="4">
            {present.flatMap((field) =>
              labelledLeaves(field).map((entry) => (
                <Stack key={entry.path.join(".")} spacing="1">
                  <Text fontSize="xs" color="smMuted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide">
                    {leafLabel(entry.path)}
                  </Text>
                  <Box>
                    {isExtensionList(entry) ? (
                      <Text fontSize="sm" fontWeight="semibold" color="smText" whiteSpace="pre-wrap">
                        {formatLeafValue(entry, formatOptions)}
                      </Text>
                    ) : isAwardCriteria(entry) ? (
                      <AwardCriteriaView
                        proposed={entry.value}
                        current={currentValueAt(current, entry.path)}
                        opts={formatOptions}
                        replacedInFull={comparisonFailed}
                        leafLabel={leafLabel}
                      />
                    ) : Array.isArray(entry.value) ? (
                      <ListValueView
                        entry={entry}
                        current={currentValueAt(current, entry.path)}
                        opts={formatOptions}
                        replacedInFull={comparisonFailed}
                        leafLabel={leafLabel}
                      />
                    ) : editorKindFor(entry, body?.schema) !== "readOnly" ? (
                      <EditableValue
                        kind={editorKindFor(entry, body?.schema)}
                        label={leafLabel(entry.path)}
                        value={editedValue(edits, entry.path, entry.value)}
                        isEmptiedRequired={isEmptiedRequired(entry.path)}
                        choices={choicesOnCard(body?.schema, entry.path, entry.value, current, payload, comparison)}
                        choiceLabel={(choice) => choiceLabelAt(entry.path, choice)}
                        onChange={(value) => edit(entry.path, value)}
                      />
                    ) : (
                      <Text fontSize="sm" fontWeight="semibold" color="smText" whiteSpace="pre-wrap">
                        {formatLeafValue(entry, formatOptions)}
                      </Text>
                    )}
                    <ConfirmedDeadline
                      entry={entry}
                      proposalAt={(path) => editedValue(edits, path, currentValueAt(payload, path))}
                      currentAt={(path) => currentValueAt(current, path)}
                      leafLabel={leafLabel}
                      opts={formatOptions}
                    />
                  </Box>
                </Stack>
              )),
            )}
          </Stack>
        </Box>
      )}

      {filledIn.length > 0 && (
        <Box mt="3" data-testid="filled-in-by-assistant">
          <Text fontSize="sm" fontWeight="semibold" color="smText">
            {t("Filled in by the assistant")}
          </Text>
          {filledIn.map(({ field, value }) => (
            <Text key={field} fontSize="sm" color="smText">
              {filledInText(field, value)}
            </Text>
          ))}
        </Box>
      )}

      {showsSources && (
        <FieldSourcesView
          claims={claims}
          assistantFilled={filledIn.map(({ field }) => field)}
          changed={body ? args[body.name] : args}
          leafLabel={leafLabel}
          libraryTitle={libraryTitle}
          rulesSetProcedure={!isPrivate && setsProcedureOrDeadline(payload)}
        />
      )}

      {comparisonPending && (
        <Text fontSize="sm" color="smMuted" mt="2">
          {t("Fetching the saved values to compare …")}
        </Text>
      )}

      {comparisonFailed && (
        <Text fontSize="sm" color="smWarning" mt="2">
          {t("The saved values could not be fetched, so the card cannot show what is removed. Lists are replaced in full.")}
        </Text>
      )}

      {otherChanges.length > 0 && (
        <OtherChanges entries={otherChanges} leafLabel={leafLabel} opts={formatOptions} />
      )}

      {present.length === 0 && concerns.length === 0 && otherChanges.length === 0 && !showsSources && (
        <Text fontSize="sm" color="smTextDisabled">
          {t("Confirm this action?")}
        </Text>
      )}

      {missing.length > 0 && (
        <Text fontSize="xs" color="smError" mt="3">
          {t("Missing")}: {missing.map((path) => leafLabel(path)).join(", ")} —{" "}
          {t("tell me in the chat and I'll add it.")}
        </Text>
      )}

      {(weightDepartures.length > 0 || departsFromRequest.length > 0 || weightSum !== null) && (
        <Box mt="3" data-testid="departs-from-request">
          {weightDepartures.length > 0 && (
            <>
              <Text fontSize="sm" fontWeight="semibold" color="smWarning">
                {weightDepartures.some((departure) => "requested" in departure)
                  ? t("This card proposes other weights than you asked for")
                  : t("This card changes these weights")}
              </Text>
              {weightDepartures.map((departure) => (
                <Text key={departure.index} fontSize="sm" color="smWarning">
                  {departureText(departure)}
                </Text>
              ))}
            </>
          )}
          {departsFromRequest.length > 0 && (
            <>
              <Text fontSize="sm" fontWeight="semibold" color="smWarning">
                {t("What you asked for cannot be saved, so this card proposes other values")}
              </Text>
              {departsFromRequest.map((reason) => (
                <Text key={`${reason.field ?? ""}:${reason.code}`} fontSize="sm" color="smWarning">
                  {reason.field ? `${leafLabel(reason.field.split("."))}: ` : ""}
                  {fieldErrorText(reason)}
                </Text>
              ))}
            </>
          )}
          {weightSum !== null && (
            <>
              <Text fontSize="sm" color="smWarning">
                {t("The weights on this card add up to {{sum}} %.", { sum: weightSum })}{" "}
                {t("zz_FieldErrorCode_CriterionWeightsMustSumTo100")}
              </Text>
              {!refused && (
                <Text fontSize="xs" color="smMuted" mt="1">
                  {t("Tell me in the chat what to change, and I will make a new proposal.")}
                </Text>
              )}
            </>
          )}
        </Box>
      )}

      {refused && (
        <Box mt="3" data-testid="refused-proposal">
          <Text fontSize="sm" fontWeight="semibold" color="smError">
            {t("This proposal cannot be saved as it stands")}
          </Text>
          {pending.refusedReasons?.map((reason) => (
            <Text key={`${reason.field ?? ""}:${reason.code}`} fontSize="sm" color="smError">
              {reason.field ? `${leafLabel(reason.field.split("."))}: ` : ""}
              {fieldErrorText(reason)}
            </Text>
          ))}
          <Text fontSize="xs" color="smMuted" mt="1">
            {t("Tell me in the chat what to change, and I will make a new proposal.")}
          </Text>
        </Box>
      )}

      <Flex justify="flex-start" gap="2" mt="4">
        <Button
          size="sm"
          bg="smPrimary"
          color="white"
          _hover={{ bg: "smPrimarySolid" }}
          onClick={() => {
            const finalArgs = withEdits(args, body?.name, edits);
            onConfirm(finalArgs, { proposed: valuesText(args), confirmed: valuesText(finalArgs) });
          }}
          isLoading={isBusy}
          isDisabled={
            refused || weightSum !== null || missing.length > 0 || emptiedRequired.length > 0 || comparisonPending
          }
        >
          {t("Confirm and execute")}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} isDisabled={isBusy}>
          {t("Cancel")}
        </Button>
      </Flex>
    </Box>
  );
};

const isWeightField = (field: string | undefined): boolean => !!field && /(^|\.)weight(\.|$)/.test(field);

const percent = (value: number): string => `${value.toLocaleString("nb-NO")} %`;

const EditableValue = ({
  kind,
  label,
  value,
  isEmptiedRequired,
  choices: { choices, draftRead },
  choiceLabel,
  onChange,
}: {
  kind: EditorKind;
  label: string;
  value: unknown;
  isEmptiedRequired: boolean;
  choices: ChoicesOnCard;
  choiceLabel: (choice: string) => string;
  onChange: (value: unknown) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Box>
      <EditorFor
        kind={kind}
        label={label}
        value={value}
        isInvalid={isEmptiedRequired}
        isDisabled={draftRead === "loading"}
        choices={choices}
        choiceLabel={choiceLabel}
        onChange={onChange}
      />
      {isEmptiedRequired && (
        <Text fontSize="xs" color="smError" mt="1">
          {t("zz_FieldErrorCode_Required")}
        </Text>
      )}
      {draftRead === "loading" && (
        <Text fontSize="xs" color="smMuted" mt="1">
          {t("Fetching the procedures this draft offers …")}
        </Text>
      )}
      {draftRead === "failed" && (
        <Text fontSize="xs" color="smWarning" mt="1">
          {t("The procedures this draft offers could not be fetched, so every procedure is shown.")}
        </Text>
      )}
    </Box>
  );
};

const EditorFor = ({
  kind,
  label,
  value,
  isInvalid,
  isDisabled,
  choices,
  choiceLabel,
  onChange,
}: {
  kind: EditorKind;
  label: string;
  value: unknown;
  isInvalid: boolean;
  isDisabled: boolean;
  choices: string[];
  choiceLabel: (choice: string) => string;
  onChange: (value: unknown) => void;
}) => {
  const common = { size: "sm" as const, "aria-label": label, bg: "smBackground", isInvalid, isDisabled };
  switch (kind) {
    case "longText":
      return (
        <AutoResizeTextarea {...common} rows={3} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />
      );
    case "number":
      return (
        <NumericInput
          {...common}
          numericValue={typeof value === "number" ? value : null}
          onNumericChange={onChange}
        />
      );
    case "money": {
      const money = isMoney(value) ? value : null;
      return (
        <InputGroup size="sm">
          <NumericInput
            {...common}
            numericValue={money?.value ?? null}
            onNumericChange={(amount) => onChange({ value: amount, currency: money?.currency ?? "NOK" })}
          />
          <InputRightAddon>{money?.currency}</InputRightAddon>
        </InputGroup>
      );
    }
    case "date":
      return (
        <ThemedDatePicker
          ariaLabel={label}
          date={typeof value === "string" && value ? value : null}
          allowPastDates
          isClearable
          onChange={(date) => onChange(date ?? "")}
        />
      );
    case "dateTime":
      return (
        <ThemedDateTimePicker
          ariaLabel={label}
          date={typeof value === "string" && value ? value : null}
          onChange={(isoDateTime) => onChange(isoDateTime ?? "")}
        />
      );
    case "yesNo":
      return <Switch size="sm" aria-label={label} isChecked={value === true} onChange={(e) => onChange(e.target.checked)} />;
    case "choice":
      return (
        <Select {...common} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
          {choices.map((choice) => (
            <option key={choice} value={choice}>
              {choiceLabel(choice)}
            </option>
          ))}
        </Select>
      );
    default:
      return <Input {...common} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />;
  }
};

const ChangedField = ({
  field,
  opts,
  leafLabel,
}: {
  field: LeafEntry;
  opts: FormatLeafOptions;
  leafLabel: (path: string[]) => string;
}) => {
  const value = formatChangedLeafValue(field, opts);
  if (!field.cleared && typeof field.value === "string" && isRichTextDocument(field.value)) {
    return (
      <Box pl="3">
        <Text fontSize="sm" color="smMuted">
          {leafLabel(field.path)}
        </Text>
        <RichValue text={field.value} />
      </Box>
    );
  }
  return (
    <Text fontSize="sm" color="smText" pl="3" whiteSpace="pre-wrap">
      {leafLabel(field.path)}: {value}
    </Text>
  );
};

const RichValue = ({ text }: { text: string }) => (
  <Text as="div" fontSize="sm" color="smText" whiteSpace="pre-wrap">
    {/<[a-z][\s\S]*>/i.test(text) || isRichTextDocument(text) ? (
      <TipTap content={text} editable={false} hasBorder={false} padding="0" maxHeight="none" />
    ) : (
      text
    )}
  </Text>
);

const ConfirmedDeadline = ({
  entry,
  proposalAt,
  currentAt,
  leafLabel,
  opts,
}: {
  entry: LeafEntry;
  proposalAt: (path: string[]) => unknown;
  currentAt: (path: string[]) => unknown;
  leafLabel: (path: string[]) => string;
  opts: FormatLeafOptions;
}) => {
  const path = confirmedDeadlinePath(entry);
  if (!path) return null;
  const deadline = proposalAt(path) ?? currentAt(path);
  if (typeof deadline !== "string" || !deadline) return null;
  return (
    <Text fontSize="sm" color="smMuted">
      {leafLabel(path)}: {formatLeafValue({ path, value: deadline }, opts)}
    </Text>
  );
};

const OtherChanges = ({
  entries,
  leafLabel,
  opts,
}: {
  entries: LeafEntry[];
  leafLabel: (path: string[]) => string;
  opts: FormatLeafOptions;
}) => {
  const { t } = useTranslation();
  const { isOpen, onToggle } = useDisclosure();
  return (
    <Box mt="2">
      <Button size="xs" variant="link" color="smMuted" onClick={onToggle} aria-expanded={isOpen}>
        {isOpen
          ? t("Hide the other fields that change")
          : t("Show the other fields that change ({{count}})", { count: entries.length })}
      </Button>
      <Collapse in={isOpen} animateOpacity unmountOnExit>
        <Stack spacing="1" mt="2">
          {entries.map((entry) => (
            <Text key={entry.path.join(".")} fontSize="sm" color="smText" whiteSpace="pre-wrap">
              <Text as="span" color="smMuted">
                {leafLabel(entry.path)}:
              </Text>{" "}
              {formatLeafValue(entry, opts)}
            </Text>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};
